import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Package, Search, RefreshCw, Calendar, User, ChevronUp, ChevronDown, Plus, ChevronRight, X } from 'lucide-react';
import api from 'services/api';
import { formatarMoeda } from '../utils';

const ListaLotes = ({ 
  onLoteClick, 
  showNotification, 
  refreshKey,
  onAtivarModoSelecao,
  onDesativarModoSelecao,
  onAbrirModalCriarLote,
  modoSelecao = false,
  canAuthorizePayment = false,
  contasSelecionadas = []
}) => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    nome: '',
  });
  const [nomeBusca, setNomeBusca] = useState('');
  const [manualSearchTick, setManualSearchTick] = useState(0);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  // Estado para controlar se está minimizado
  const [minimizado, setMinimizado] = useState(() => {
    try {
      const saved = localStorage.getItem('lotes_contas_pagar_minimizado');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Salvar preferência no localStorage quando mudar
  useEffect(() => {
    try {
      localStorage.setItem('lotes_contas_pagar_minimizado', JSON.stringify(minimizado));
    } catch (error) {
      console.warn('Erro ao salvar preferência de lotes:', error);
    }
  }, [minimizado]);

  const carregarLotes = useCallback(async (page = 1, perPage = pagination.per_page, filtrosAtuais = filtros) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        ...filtrosAtuais,
      };

      // Remover filtros vazios
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await api.get('/n8n/pagamento-lotes', { params });

      // Laravel paginator retorna diretamente: { data: [...], current_page, last_page, etc }
      if (response.data && response.data.data) {
        setLotes(response.data.data);
        setPagination({
          current_page: response.data.current_page || 1,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 15,
          total: response.data.total || 0,
          from: response.data.from || null,
          to: response.data.to || null,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
      if (showNotification) {
        showNotification('Erro ao carregar lotes', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.per_page, filtros, showNotification]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nomeNormalizado = nomeBusca.trim();
      setFiltros((prev) => (
        prev.nome === nomeNormalizado
          ? prev
          : { ...prev, nome: nomeNormalizado }
      ));
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [nomeBusca]);

  useEffect(() => {
    carregarLotes(1);
  }, [refreshKey, filtros.nome, pagination.per_page, manualSearchTick, carregarLotes]); // Recarregar em filtros, paginação e refresh

  const handleBuscar = () => {
    const nomeNormalizado = nomeBusca.trim();
    setFiltros((prev) => ({ ...prev, nome: nomeNormalizado }));
    setManualSearchTick((prev) => prev + 1);
  };

  const limparFiltros = () => {
    setNomeBusca('');
    setFiltros({ nome: '' });
  };

  const formatarData = (data) => {
    if (!data) return 'N/A';
    try {
      return new Date(data).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return data;
    }
  };

  const filtrosAtivos = useMemo(() => {
    const ativos = [];
    if (filtros.nome) {
      ativos.push({
        key: 'nome',
        label: `Nome: ${filtros.nome}`,
      });
    }
    return ativos;
  }, [filtros.nome]);

  const resumo = useMemo(() => {
    const totalLotes = pagination.total || 0;
    const lotesNaPagina = lotes.length;
    const valorPagina = lotes.reduce((acc, lote) => acc + (parseFloat(lote.total_valor) || 0), 0);
    const autorizadosPagina = lotes.reduce((acc, lote) => acc + (Number(lote.total_autorizados) || 0), 0);
    return { totalLotes, lotesNaPagina, valorPagina, autorizadosPagina };
  }, [lotes, pagination.total]);

  const temFiltrosAtivos = filtrosAtivos.length > 0;
  const skeletonRows = Array.from({ length: 4 });

  return (
    <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
      {/* Header */}
      <div className="p-4 border-b border-[color:var(--border)]">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center m-0">
              <Package className="h-5 w-5 mr-2 text-[color:var(--accent)] flex-shrink-0" />
              Lotes de Pagamento
            </h2>
          </div>

          <div className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end">
            {canAuthorizePayment && !modoSelecao && onAtivarModoSelecao && (
              <button
                onClick={onAtivarModoSelecao}
                className="inline-flex min-h-[44px] items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                <Plus className="h-4 w-4 flex-shrink-0" />
                Criar Lote
              </button>
            )}

            <button
              onClick={() => setMinimizado(!minimizado)}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center p-2 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              title={minimizado ? 'Mostrar lotes' : 'Ocultar lotes'}
              aria-expanded={!minimizado}
              aria-controls="lista-lotes-filtros lista-lotes-conteudo"
              aria-label={minimizado ? 'Expandir seção de lotes' : 'Recolher seção de lotes'}
            >
              {minimizado ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            <button
              onClick={() => carregarLotes(pagination.current_page)}
              disabled={loading}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center p-2 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] rounded-md transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              title="Atualizar"
              aria-label="Atualizar lotes"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-xs sm:grid-cols-4 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
          <p className="font-medium text-[color:var(--text-primary)] sm:col-span-1">
            Lotes:
            <span className="ml-1 text-[color:var(--text-muted)]">{resumo.totalLotes}</span>
          </p>
          <p className="font-medium text-[color:var(--text-primary)] sm:col-span-1">
            Nesta página:
            <span className="ml-1 text-[color:var(--text-muted)]">{resumo.lotesNaPagina}</span>
          </p>
          <p className="font-medium text-[color:var(--text-primary)] sm:col-span-1">
            Valor da página:
            <span className="ml-1 text-[color:var(--text-muted)]">{formatarMoeda(resumo.valorPagina)}</span>
          </p>
          <p className="font-medium text-[color:var(--text-primary)] sm:col-span-1">
            Autorizados (página):
            <span className="ml-1 text-[color:var(--text-muted)]">{resumo.autorizadosPagina}</span>
          </p>
        </div>

        {/* Botões quando está em modo seleção */}
        {canAuthorizePayment && modoSelecao && (
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <button
              onClick={onDesativarModoSelecao}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
            >
              Cancelar Seleção
            </button>
            <button
              onClick={onAbrirModalCriarLote}
              disabled={contasSelecionadas.length === 0}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
            >
              Criar Lote ({contasSelecionadas.length})
            </button>
          </div>
        )}

        {/* Filtros - com animação de colapso */}
        <div 
          id="lista-lotes-filtros"
          aria-hidden={minimizado}
          inert={minimizado}
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            minimizado ? 'max-h-0 opacity-0 pointer-events-none' : 'max-h-[2000px] opacity-100'
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-medium text-[color:var(--text-primary)] mb-1">
                Nome
              </label>
              <input
                type="text"
                value={nomeBusca}
                onChange={(e) => setNomeBusca(e.target.value)}
                placeholder="Buscar pelo nome do lote..."
                className="w-full min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
              />
            </div>
            <div className="hidden md:flex items-end">
              <button
                onClick={handleBuscar}
                className="w-full min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] flex items-center justify-center"
              >
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </button>
            </div>
          </div>

          {temFiltrosAtivos && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {filtrosAtivos.map((filtroAtivo) => (
                <button
                  key={filtroAtivo.key}
                  type="button"
                  onClick={() => {
                    setNomeBusca('');
                    setFiltros({ nome: '' });
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] hover:border-[color:var(--accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                  aria-label={`Remover filtro ${filtroAtivo.label}`}
                >
                  {filtroAtivo.label}
                  <X className="h-3 w-3" />
                </button>
              ))}
              <button
                type="button"
                onClick={limparFiltros}
                className="text-xs font-medium text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] rounded-sm"
              >
                Limpar tudo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista de Lotes - com animação de colapso */}
      <div 
        id="lista-lotes-conteudo"
        aria-hidden={minimizado}
        inert={minimizado}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          minimizado ? 'max-h-0 opacity-0 pointer-events-none' : 'max-h-[2000px] opacity-100'
        }`}
      >
        <div className="divide-y divide-[color:var(--border)]">
        {loading ? (
          <div className="p-4 space-y-3" aria-live="polite" aria-busy="true">
            {skeletonRows.map((_, index) => (
              <div
                key={`skeleton-lote-${index}`}
                className="border border-[color:var(--border)] rounded-lg p-4 animate-pulse bg-[color:var(--surface-muted)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="h-3 w-16 rounded bg-[color:var(--surface)] mb-2" />
                    <div className="h-4 w-2/3 rounded bg-[color:var(--surface)] mb-3" />
                    <div className="h-3 w-full max-w-md rounded bg-[color:var(--surface)]" />
                  </div>
                  <div className="w-28">
                    <div className="h-4 w-24 rounded bg-[color:var(--surface)] mb-2 ml-auto" />
                    <div className="h-6 w-full rounded-full bg-[color:var(--surface)]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : lotes.length === 0 ? (
          <div className="p-8 text-center text-[color:var(--text-muted)]">
            <Package className="h-12 w-12 mx-auto mb-3 text-[color:var(--text-muted)]" />
            <p className="text-sm text-[color:var(--text-primary)] font-medium mb-1">Nenhum lote encontrado com os critérios atuais.</p>
            <p className="text-xs text-[color:var(--text-muted)] mb-4">
              {temFiltrosAtivos ? 'Ajuste ou limpe os filtros para ampliar os resultados.' : 'Crie um lote para iniciar a gestão de pagamentos em lote.'}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2">
              {temFiltrosAtivos && (
                <button
                  type="button"
                  onClick={limparFiltros}
                  className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                >
                  Limpar filtros
                </button>
              )}
              {canAuthorizePayment && !modoSelecao && onAtivarModoSelecao && (
                <button
                  type="button"
                  onClick={onAtivarModoSelecao}
                  className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                >
                  <Plus className="h-4 w-4" />
                  Criar lote
                </button>
              )}
            </div>
          </div>
        ) : (
          lotes.map((lote) => {
            return (
              <button
                type="button"
                key={lote.id}
                className="group w-full p-4 text-left hover:bg-[color:var(--surface-muted)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-inset"
                onClick={() => onLoteClick && onLoteClick(lote.id)}
                aria-label={`Abrir detalhes do lote ${lote.id}${lote.nome ? ` - ${lote.nome}` : ''}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-[color:var(--text-muted)]">#{lote.id}</span>
                    </div>
                    {lote.nome && (
                      <h3 className="text-sm font-semibold text-[color:var(--text-primary)] mb-1 truncate">{lote.nome}</h3>
                    )}
                    <div className="text-sm font-semibold text-[color:var(--text-primary)] sm:hidden">
                      {formatarMoeda(parseFloat(lote.total_valor) || 0)}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-[color:var(--text-muted)]">
                      <div className="flex items-center">
                        <Package className="h-3 w-3 mr-1" />
                        {lote.total_itens || 0} itens
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatarData(lote.created_at)}
                      </div>
                      {lote.created_by && (
                        <div className="flex items-center">
                          <User className="h-3 w-3 mr-1" />
                          {lote.created_by.full_name || lote.created_by.name || 'N/A'}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="lg:ml-4 lg:text-right">
                    <p className="hidden lg:block text-sm font-semibold text-[color:var(--text-primary)]">
                      {formatarMoeda(parseFloat(lote.total_valor) || 0)}
                    </p>
                    <span className="mt-2 inline-flex items-center justify-end gap-2">
                      <span className="text-xs font-medium text-[color:var(--accent)]">
                        Ver lote
                      </span>
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-all group-hover:border-[color:var(--accent)] group-hover:text-[color:var(--accent)]">
                        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                      </span>
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
        </div>

        {/* Paginação */}
        {pagination.total > 0 && (
          <div className="p-4 border-t border-[color:var(--border)] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-[color:var(--text-muted)]">
              Mostrando {pagination.from || 0} a {pagination.to || 0} de {pagination.total} lotes
            </div>
            <div className="hidden md:flex flex-wrap items-center gap-2">
              <label className="hidden md:inline-flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
                Itens por página
                <select
                  value={pagination.per_page}
                  onChange={(e) => {
                    const novoPerPage = Number(e.target.value) || 15;
                    setPagination((prev) => ({ ...prev, per_page: novoPerPage, current_page: 1 }));
                  }}
                  className="px-2 py-1 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
                >
                  {[10, 15, 25, 50].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <button
                onClick={() => carregarLotes(pagination.current_page - 1)}
                disabled={pagination.current_page === 1 || loading || pagination.last_page <= 1}
                className="px-3 py-1 text-sm border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm text-[color:var(--text-primary)]">
                Página {pagination.current_page} de {pagination.last_page}
              </span>
              <button
                onClick={() => carregarLotes(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page || loading || pagination.last_page <= 1}
                className="px-3 py-1 text-sm border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Próxima
              </button>
            </div>

            <div className="md:hidden space-y-2">
              <div className="text-center text-xs font-medium text-[color:var(--text-primary)]">
                Página {pagination.current_page} de {pagination.last_page}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => carregarLotes(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1 || loading || pagination.last_page <= 1}
                  className="min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                  aria-label="Ir para página anterior de lotes"
                >
                  Anterior
                </button>
                <button
                  onClick={() => carregarLotes(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page || loading || pagination.last_page <= 1}
                  className="min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                  aria-label="Ir para próxima página de lotes"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaLotes;
